import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { saasListPlans, saasMySubscription, saasCheckout, saasStartTrial } from '../api';
import { toast } from '../hooks/use-toast';
import { Check, Sparkles, Zap, Crown, ArrowRight, Gift } from 'lucide-react';

const PLAN_META = {
  starter:    { icon: Sparkles, en: { name: 'Starter',    tag: 'For solo founders' },        ar: { name: 'المبتدئ',    tag: 'للأفراد وأصحاب الأعمال الصغيرة' } },
  growth:     { icon: Zap,      en: { name: 'Growth',     tag: 'For growing companies' },     ar: { name: 'النمو',      tag: 'للشركات النامية', highlight: true }, popular: true },
  enterprise: { icon: Crown,    en: { name: 'Enterprise', tag: 'For accounting teams' },      ar: { name: 'المؤسسات',   tag: 'لفرق المحاسبة والشركات الكبرى' } }
};

const FEATURES = {
  starter: {
    en: ['50 transactions / month', '1 company', 'AI transaction categorisation', 'CSV & PDF upload', 'Basic P&L & Cashflow', 'Email support'],
    ar: ['٥٠ معاملة / شهر', 'شركة واحدة', 'تصنيف ذكي للعمليات', 'رفع CSV وPDF', 'تقرير أرباح وتدفق نقدي', 'دعم بالبريد']
  },
  growth: {
    en: ['500 transactions / month', 'Up to 3 companies', 'Everything in Starter', 'Full VAT return generation', 'Bank reconciliation', 'Invoice matching', 'Priority chat support'],
    ar: ['٥٠٠ معاملة / شهر', 'حتى ٣ شركات', 'كل مزايا المبتدئ', 'إعداد إقرار VAT كامل', 'مطابقة بنكية', 'مطابقة الفواتير', 'دعم أولوية']
  },
  enterprise: {
    en: ['Unlimited transactions', 'Unlimited companies', 'Everything in Growth', 'CFO reports + forecasting', 'Corporate Tax filing', 'Dedicated account manager', 'API access & data export'],
    ar: ['معاملات غير محدودة', 'شركات غير محدودة', 'كل مزايا النمو', 'تقارير CFO وتوقعات', 'إعداد ضريبة الشركات', 'مدير حساب مخصص', 'وصول API وتصدير']
  }
};

const AccountingPricing = () => {
  const { lang, dir } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await saasListPlans();
        setPlans(p.plans || []);
        if (user) {
          try { setCurrent(await saasMySubscription()); } catch { /* not subscribed yet */ }
        }
      } finally { setLoading(false); }
    })();
  }, [user]);

  const subscribe = async (planId) => {
    if (!user) { navigate('/login?redirect=/accounting/pricing'); return; }
    setPurchasing(planId);
    try {
      const { checkout_url } = await saasCheckout(planId, lang);
      window.location.href = checkout_url;
    } catch (e) {
      toast({ title: lang === 'ar' ? 'فشل الدفع' : 'Checkout failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
      setPurchasing(null);
    }
  };

  const startTrial = async () => {
    if (!user) { navigate('/login?redirect=/accounting/pricing'); return; }
    setPurchasing('trial');
    try {
      await saasStartTrial('growth');
      toast({ title: lang === 'ar' ? '🎉 بدأت تجربتك المجانية!' : '🎉 Free trial started!', description: lang === 'ar' ? '١٤ يوماً كاملة على باقة Growth بدون بطاقة' : '14 full days on Growth — no card required' });
      setTimeout(() => navigate('/accounting'), 900);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
      setPurchasing(null);
    }
  };

  const trialUsed = current?.trial_used || current?.status === 'trialing' || current?.status === 'active';

  return (
    <div className="pt-28 pb-20 min-h-screen bg-gradient-to-b from-white to-[#f8fafc]" dir={dir}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-4">{lang === 'ar' ? '— المحاسبة الذكية —' : '— AI Accounting —'}</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#0a0a0a] leading-tight">{lang === 'ar' ? 'محاسب ذكي في جيبك،' : 'A CFO in your pocket,'}</h1>
          <h1 className="font-display text-5xl md:text-6xl text-[#2563eb] leading-tight italic">{lang === 'ar' ? 'اشتراك شهري بسيط.' : 'for one monthly fee.'}</h1>
          <p className="mt-5 text-[#111827]/70 max-w-2xl mx-auto">{lang === 'ar' ? 'ارفع كشف الحساب البنكي، والذكاء الاصطناعي يصنّف كل عملية، يطابقها مع فواتيرك، ويصدر التقارير المحاسبية بضغطة زر.' : 'Upload your bank statement, our AI categorises every transaction, reconciles against invoices, and generates every accounting report you need.'}</p>
        </div>

        {!trialUsed && (
          <div className="max-w-3xl mx-auto mb-10 rounded-2xl border-2 border-dashed border-[#2563eb] bg-blue-50/50 p-6 flex flex-wrap items-center gap-4 justify-between" data-testid="trial-banner-pricing">
            <div className="flex items-center gap-4 flex-1 min-w-[240px]">
              <div className="w-12 h-12 rounded-xl bg-[#2563eb] text-white flex items-center justify-center flex-shrink-0">
                <Gift className="w-6 h-6" />
              </div>
              <div>
                <div className="font-display text-xl text-[#0a0a0a]">
                  {lang === 'ar' ? 'جرّب Growth مجاناً لمدة ١٤ يوم' : 'Try Growth free for 14 days'}
                </div>
                <div className="text-xs text-[#111827]/60 mt-0.5">
                  {lang === 'ar' ? 'بدون بطاقة ائتمان · ألغِ في أي وقت · الوصول لكل ميزات Growth' : 'No credit card · Cancel anytime · Full access to Growth features'}
                </div>
              </div>
            </div>
            <Button
              onClick={startTrial}
              disabled={purchasing === 'trial'}
              data-testid="start-trial-btn"
              className="gold-button rounded-md h-11 px-6 text-sm font-bold"
            >
              {purchasing === 'trial'
                ? '…'
                : (<>{lang === 'ar' ? 'ابدأ تجربتك المجانية' : 'Start free trial'} <ArrowRight className="w-4 h-4 ms-2 rtl:hidden" /></>)}
            </Button>
          </div>
        )}

        {loading ? <div className="text-center text-[#111827]/40">…</div> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((p) => {
              const meta = PLAN_META[p.id];
              const Icon = meta.icon;
              const isCurrent = current?.plan === p.id && current?.status === 'active';
              const popular = meta.popular;
              return (
                <div key={p.id} data-testid={`plan-${p.id}`} className={`relative rounded-2xl border p-8 flex flex-col ${popular ? 'border-[#2563eb] shadow-2xl shadow-[#2563eb]/10 md:-translate-y-3 bg-white' : 'border-[#e5e7eb] bg-white'}`}>
                  {popular && <div className="absolute -top-3 start-6 bg-[#2563eb] text-white text-[10px] px-3 py-1 rounded-full uppercase tracking-widest font-bold">{lang === 'ar' ? 'الأكثر شعبية' : 'Most Popular'}</div>}
                  {isCurrent && <div className="absolute -top-3 end-6 bg-[#3b82f6] text-white text-[10px] px-3 py-1 rounded-full uppercase tracking-widest font-bold">{lang === 'ar' ? 'باقتك الحالية' : 'Your Plan'}</div>}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${popular ? 'bg-[#2563eb] text-white' : 'bg-[#f8fafc] text-[#2563eb]'}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="font-display text-2xl text-[#0a0a0a]">{meta[lang].name}</div>
                  <div className="text-sm text-[#111827]/60 mt-1">{meta[lang].tag}</div>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-display text-5xl text-[#0a0a0a]">{p.price_aed}</span>
                    <span className="text-sm text-[#111827]/60">AED</span>
                    <span className="text-sm text-[#111827]/60">/{lang === 'ar' ? 'شهر' : 'mo'}</span>
                  </div>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {FEATURES[p.id][lang].map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#111827]/80">
                        <Check className="w-4 h-4 text-[#2563eb] mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => subscribe(p.id)}
                    disabled={isCurrent || purchasing === p.id}
                    data-testid={`subscribe-${p.id}`}
                    className={`mt-8 h-12 rounded-md text-sm font-semibold ${popular ? 'gold-button' : 'ghost-button bg-transparent'}`}
                  >
                    {isCurrent ? (lang === 'ar' ? 'مفعّل' : 'Active') : purchasing === p.id ? '…' : (lang === 'ar' ? 'اشترك الآن — ادفع بالبطاقة' : 'Subscribe — Pay with card')} {!isCurrent && purchasing !== p.id && <ArrowRight className="w-4 h-4 ms-2 rtl:hidden" />}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-14 text-xs text-[#111827]/50">
          {lang === 'ar' ? 'كل الأسعار بالدرهم الإماراتي شاملة الضريبة. دفع آمن عبر Geidea (Visa / Mastercard). الاشتراك لمدة شهر ويمكنك تجديده في أي وقت.' : 'All prices in AED VAT-inclusive. Secure payment via Geidea (Visa / Mastercard). Subscriptions run for one month and can be renewed anytime.'}
        </div>
      </div>
    </div>
  );
};

export default AccountingPricing;
